import React, { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { API, apiClient as axios, exportLeadsCsv, exportSalesSummaryCsv, getProductSalesSummary } from "../../lib/api";
import { Toast } from "../../components/ui";
import { Download, RefreshCw, Loader2 } from "lucide-react";

export default function Analytics({ token }) {
  const [filters, setFilters] = useState({
    segment: "",
    status: "",
    leadmaker: "",
    product_id: "",
    start_date: "",
    end_date: "",
  });
  const [exportType, setExportType] = useState("leads");
  const [exporting, setExporting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [salesSummary, setSalesSummary] = useState([]);
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // Load users for leadmaker filter
  const loadUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/users`, { headers: authHeaders });
      setUsers(res.data?.data || []);
    } catch (e) {
      console.error("Failed to load users", e);
    }
  }, [authHeaders]);

  // Load products for product filter
  const loadProducts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/products`, { headers: authHeaders });
      setProducts(res.data?.data || []);
    } catch (e) {
      console.error("Failed to load products", e);
    }
  }, [authHeaders]);

  // Load sales summary
  const loadSalesSummary = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        start_date: filters.start_date,
        end_date: filters.end_date,
      };
      const response = await getProductSalesSummary(params, token);
      setSalesSummary(response?.data || []);
    } catch (e) {
      console.error("Failed to load sales summary", e);
      toast.error("Failed to load sales summary");
      setSalesSummary([]);
    } finally {
      setLoading(false);
    }
  }, [filters.start_date, filters.end_date, token]);

  useEffect(() => {
    loadUsers();
    loadProducts();
  }, [loadUsers, loadProducts]);

  useEffect(() => {
    loadSalesSummary();
  }, [loadSalesSummary]);

  const handleExport = async () => {
    try {
      setExporting(true);
      
      const exportParams = {
        segment: filters.segment,
        status: filters.status,
        leadmaker: filters.leadmaker,
        product_id: filters.product_id,
        start_date: filters.start_date,
        end_date: filters.end_date,
      };

      if (exportType === "leads") {
        await exportLeadsCsv(exportParams, token);
        toast.success("Leads exported successfully!");
      } else {
        await exportSalesSummaryCsv(exportParams, token);
        toast.success("Sales summary exported successfully!");
      }
    } catch (e) {
      console.error("Export failed", e);
      toast.error("Failed to export data");
    } finally {
      setExporting(false);
    }
  };

  const clearFilters = () => {
    setFilters({
      segment: "",
      status: "",
      leadmaker: "",
      product_id: "",
      start_date: "",
      end_date: "",
    });
  };

  return (
    <div className="page">
      <Toast />
      
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white">Analytics & Export</h1>
      </div>

      {/* Filters Section */}
      <div className="card mb-6">
        <h2 className="text-lg font-semibold text-white mb-4">Filters</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Segment Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Customer Segment
            </label>
            <select
              className="input w-full"
              value={filters.segment}
              onChange={(e) => setFilters({ ...filters, segment: e.target.value })}
            >
              <option value="">All Segments</option>
              <option value="retailer">Retailer</option>
              <option value="wholesale">Wholesale</option>
              <option value="hotel">Hotel</option>
              <option value="restaurant">Restaurant</option>
              <option value="event">Event</option>
              <option value="canteen">Canteen</option>
              <option value="family">Family</option>
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Lead Status
            </label>
            <select
              className="input w-full"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          {/* Leadmaker Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Lead Maker
            </label>
            <select
              className="input w-full"
              value={filters.leadmaker}
              onChange={(e) => setFilters({ ...filters, leadmaker: e.target.value })}
            >
              <option value="">All Lead Makers</option>
              {users
                .filter((u) => u.role === "leadmaker")
                .map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
            </select>
          </div>

          {/* Product Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Product
            </label>
            <select
              className="input w-full"
              value={filters.product_id}
              onChange={(e) => setFilters({ ...filters, product_id: e.target.value })}
            >
              <option value="">All Products</option>
              {products.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} - {p.brand}
                </option>
              ))}
            </select>
          </div>

          {/* Start Date */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Start Date
            </label>
            <input
              type="date"
              className="input w-full"
              value={filters.start_date}
              onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
            />
          </div>

          {/* End Date */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              End Date
            </label>
            <input
              type="date"
              className="input w-full"
              value={filters.end_date}
              onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
            />
          </div>
        </div>

        {/* Filter Actions */}
        <div className="flex gap-2 mt-4">
          <button
            onClick={clearFilters}
            className="btn-secondary flex items-center gap-2"
          >
            Clear Filters
          </button>
          <button
            onClick={loadSalesSummary}
            disabled={loading}
            className="btn-secondary flex items-center gap-2"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Refresh Summary
          </button>
        </div>
      </div>

      {/* Export Section */}
      <div className="card mb-6">
        <h2 className="text-lg font-semibold text-white mb-4">Export Data</h2>
        
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
          {/* Export Type Selector */}
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Export Type
            </label>
            <div className="flex gap-4">
            <label className="flex items-center gap-2 text-gray-300 cursor-pointer">
                <input
                  type="radio"
                  name="exportType"
                  value="leads"
                  checked={exportType === "leads"}
                  onChange={(e) => setExportType(e.target.value)}
                  className="text-blue-600"
                />
                <span>Leads</span>
              </label>
              <label className="flex items-center gap-2 text-gray-300 cursor-pointer">
                <input
                  type="radio"
                  name="exportType"
                  value="sales_summary"
                  checked={exportType === "sales_summary"}
                  onChange={(e) => setExportType(e.target.value)}
                  className="text-blue-600"
                />
                <span>Sales Summary</span>
              </label>
            </div>
          </div>

          {/* Export Button */}
          <button
            onClick={handleExport}
            disabled={exporting}
            className="btn flex items-center gap-2 mt-6 md:mt-0"
          >
            {exporting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            Export CSV
          </button>
        </div>
      </div>

      {/* Sales Summary Display */}
      <div className="card">
        <h2 className="text-lg font-semibold text-white mb-4">
          Product Sales Summary
        </h2>
        
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
          </div>
        ) : salesSummary.length === 0 ? (
          <div className="text-center text-gray-400 py-8">
            No sales data available for the selected filters
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Product Name</th>
                  <th className="text-right">Total Quantity</th>
                  <th className="text-right">Total Revenue</th>
                  <th className="text-right">Avg Unit Price</th>
                  <th className="text-right">Orders Count</th>
                </tr>
              </thead>
              <tbody>
                {salesSummary.map((row, idx) => (
                  <tr key={idx}>
                    <td className="font-medium">{row.p_name || row.name}</td>
                    <td className="text-right">{Number(row.total_quantity || 0).toLocaleString()}</td>
                    <td className="text-right">
                      ₹{Number(row.total_revenue || 0).toFixed(2)}
                    </td>
                    <td className="text-right">
                      ₹{Number(row.avg_unit_price || 0).toFixed(2)}
                    </td>
                    <td className="text-right">{Number(row.total_sales || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

